import {
  CheckCircledIcon,
  CrossCircledIcon,
  Pencil1Icon,
} from "@radix-ui/react-icons";
import { ColumnDef } from "@tanstack/react-table";
import { useMemo } from "react";
import { useSearchParams } from "react-router-dom";

import { ICustomMedication } from "../../../@types/admin/AdminMedication";
import { TableSorted } from "../../../components/TableSorted";
import { useReactivateCustomMedication } from "../../../hooks/mutations/admin/customMedications";
import {
  CUSTOM_MEDICATIONS_QUERY_KEY,
  useCustomMedications,
} from "../../../hooks/queries/admin/customMedications";
import { useHospitalOptions } from "../../../hooks/queries/admin/selectValues";
import { useModal } from "../../../hooks/useModal";
import { useToast } from "../../../hooks/useToast";
import { getLongFormattedDate } from "../../../utils/dateFormatter";
import { errorToastMessage } from "../../../utils/HttpError";
import { RowActions } from "../Patients/AdminPatients.styles";
import {
  AdminSelectField,
  ErrorMessage,
  FieldHint,
  FilterButton,
  FilterGroup,
  FilterLabel,
  FiltersContainer,
  PageContainer,
  PageHeader,
  PageInfoTooltip,
  PageTitle,
  PageTitleGroup,
  RowAction,
  SearchInput,
  StatusBadge,
  TableContainer,
  TableLoadingState,
  TablePagination,
  TableWrapper,
  useMockPagination,
} from "../shared";
import { CustomMedicationModal } from "./CustomMedicationModal";
import { LanguageCodes } from "./CustomMedications.styles";
import { DeactivateCustomMedicationModal } from "./DeactivateCustomMedicationModal";

/** Table rows carry every name joined, so the list search finds any language. */
interface ICustomMedicationRow extends ICustomMedication {
  allNames: string;
}

const SEARCH_FIELDS: (keyof ICustomMedicationRow)[] = ["allNames"];

const MODAL_OPTIONS = {
  width: "min(620px, 94vw)",
  maxHeight: "90vh",
  showCloseButton: true,
};

/** `?hospitalId=` keeps the chosen hospital linkable (the hospital page links here). */
const HOSPITAL_PARAM = "hospitalId";

/**
 * Custom medications admin page (EN-5240): one hospital's study medications
 * and vitamins — items the care team defined and confirmed, which patients of
 * that hospital find in the app's medication search before the SAM or SNOMED
 * results.
 *
 * Choose the hospital first; everything below is scoped to it. Inactive items
 * are listed rather than filtered out: deactivation is reversible, and a name
 * stays taken while its item exists, so the operator needs to see it to
 * reactivate it instead of adding a twin.
 */
export function AdminCustomMedications() {
  document.title = "Custom medications - Admin";

  const [searchParams, setSearchParams] = useSearchParams();
  const parsedHospitalId = Number(searchParams.get(HOSPITAL_PARAM));
  const hospitalGroupId =
    Number.isFinite(parsedHospitalId) && parsedHospitalId > 0
      ? parsedHospitalId
      : undefined;

  const { openModal, closeModal } = useModal();
  const { showToast } = useToast();
  const { hospitals, isError: isHospitalsError } = useHospitalOptions();
  const { customMedications, isLoading, isError } =
    useCustomMedications(hospitalGroupId);
  const { reactivateCustomMedication, isReactivatingCustomMedication } =
    useReactivateCustomMedication();

  const rows: ICustomMedicationRow[] = useMemo(
    () =>
      customMedications.map((medication) => ({
        ...medication,
        allNames: (medication.names ?? []).map((n) => n.value).join(" "),
      })),
    [customMedications]
  );

  const {
    items,
    allItems,
    pagination,
    searchTerm,
    setSearchTerm,
    setPage,
    setPageSize,
  } = useMockPagination({
    data: rows,
    searchFields: SEARCH_FIELDS,
  });

  const selectHospital = (value: string) => {
    const next = new URLSearchParams(searchParams);
    if (value) next.set(HOSPITAL_PARAM, value);
    else next.delete(HOSPITAL_PARAM);
    setSearchParams(next, { replace: true });
  };

  const handleReactivate = async (medication: ICustomMedication) => {
    try {
      await reactivateCustomMedication({
        hospitalGroupId: medication.hospitalGroupId,
        id: medication.id,
      });
      showToast(`${medication.name} was reactivated.`, "success");
    } catch (error) {
      showToast(
        errorToastMessage(
          error,
          "There was an error reactivating this custom medication."
        ),
        "warning"
      );
    }
  };

  const columns: ColumnDef<ICustomMedicationRow, string>[] = useMemo(
    () => [
      { accessorKey: "name", header: "Name", enableSorting: true },
      {
        id: "languages",
        header: "Translations",
        accessorFn: (medication) =>
          (medication.names ?? []).map((n) => n.languageCode).join(", "),
        cell: ({ row }) => (
          <LanguageCodes
            title={(row.original.names ?? [])
              .map((n) => `${n.languageCode}: ${n.value}`)
              .join("\n")}
          >
            {(row.original.names ?? []).map((n) => n.languageCode).join(", ")}
          </LanguageCodes>
        ),
        enableSorting: false,
      },
      {
        id: "strength",
        header: "Strength",
        accessorFn: (medication) => medication.strength || "-",
        enableSorting: true,
      },
      {
        id: "form",
        header: "Form",
        accessorFn: (medication) => medication.formName || "-",
        enableSorting: true,
      },
      {
        id: "active",
        header: "State",
        accessorFn: (medication) => (medication.active ? "Active" : "Inactive"),
        cell: ({ row }) => (
          <StatusBadge status={row.original.active ? "active" : "inactive"}>
            {row.original.active ? "Active" : "Inactive"}
          </StatusBadge>
        ),
        enableSorting: true,
      },
      {
        accessorKey: "created",
        header: "Created",
        cell: ({ row }) => (
          <span>{getLongFormattedDate(row.original.created, "-")}</span>
        ),
        enableSorting: true,
      },
      {
        id: "actions",
        header: "",
        enableSorting: false,
        cell: ({ row }) => (
          <RowActions>
            <RowAction
              label="Edit"
              icon={<Pencil1Icon />}
              onClick={() => {
                openModal(
                  <CustomMedicationModal
                    hospitalGroupId={row.original.hospitalGroupId}
                    medication={row.original}
                    onDone={closeModal}
                  />,
                  MODAL_OPTIONS
                );
              }}
            />
            {row.original.active ? (
              <RowAction
                label="Deactivate"
                icon={<CrossCircledIcon />}
                danger
                onClick={() => {
                  openModal(
                    <DeactivateCustomMedicationModal
                      medication={row.original}
                      hospitalGroupId={row.original.hospitalGroupId}
                      onDone={closeModal}
                    />,
                    { width: "520px", showCloseButton: true }
                  );
                }}
              />
            ) : (
              <RowAction
                label="Reactivate"
                icon={<CheckCircledIcon />}
                disabled={isReactivatingCustomMedication}
                onClick={() => {
                  handleReactivate(row.original);
                }}
              />
            )}
          </RowActions>
        ),
      },
    ],
    [isReactivatingCustomMedication]
  );

  return (
    <PageContainer>
      <PageHeader>
        <PageTitleGroup>
          <PageTitle>Custom medications</PageTitle>
          <PageInfoTooltip title="Custom medications">
            Study medications and vitamins a hospital&apos;s patients can add in
            the app, from the list the care team defined and confirmed. Patients
            see them before the hospital&apos;s SAM or SNOMED results, as name,
            strength and form in their app language.
          </PageInfoTooltip>
        </PageTitleGroup>
        {hospitalGroupId !== undefined && (
          <FilterButton
            variant="primary"
            onClick={() =>
              openModal(
                <CustomMedicationModal
                  hospitalGroupId={hospitalGroupId}
                  onDone={closeModal}
                />,
                MODAL_OPTIONS
              )
            }
          >
            New medication
          </FilterButton>
        )}
      </PageHeader>

      <FiltersContainer>
        <FilterGroup>
          <FilterLabel htmlFor="custom-medications-hospital">
            Hospital
          </FilterLabel>
          <AdminSelectField
            id="custom-medications-hospital"
            value={hospitalGroupId !== undefined ? String(hospitalGroupId) : ""}
            onChange={(e) => selectHospital(e.target.value)}
          >
            <option value="">Choose a hospital</option>
            {hospitals.map((hospital) => (
              <option key={hospital.id} value={String(hospital.id)}>
                {hospital.name}
              </option>
            ))}
          </AdminSelectField>
        </FilterGroup>

        {hospitalGroupId !== undefined && (
          <SearchInput
            type="text"
            placeholder="Search by name in any language..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        )}
      </FiltersContainer>

      {isHospitalsError && (
        <ErrorMessage>Failed to load the hospitals.</ErrorMessage>
      )}

      {hospitalGroupId === undefined ? (
        <FieldHint>
          Choose a hospital to see and manage its custom medications.
        </FieldHint>
      ) : (
        <>
          {isError && (
            <ErrorMessage>
              Failed to load this hospital&apos;s custom medications. Try
              refreshing the page.
            </ErrorMessage>
          )}

          <TableContainer>
            <TableWrapper refreshKeys={[[CUSTOM_MEDICATIONS_QUERY_KEY]]}>
              <TableSorted
                columns={columns}
                data={items}
                emptyStateText={
                  isLoading ? (
                    <TableLoadingState />
                  ) : (
                    "This hospital has no custom medications"
                  )
                }
              />
            </TableWrapper>
            <TablePagination
              pagination={pagination}
              onPageChange={setPage}
              onPageSizeChange={setPageSize}
              exportCsv={{
                filename: `hospital-${hospitalGroupId}-custom-medications`,
                columns,
                rows: allItems,
              }}
            />
          </TableContainer>
        </>
      )}
    </PageContainer>
  );
}
